import { Container, Sprite, Assets, Graphics, Text, TextStyle } from 'pixi.js';
import gsap from 'gsap';
import { BaseScene } from './BaseScene';
import { COLORS, FONTS, SPACING } from '../constants/theme';
import { MusicManager } from '@services/MusicManager';
import type { LayoutMetrics } from '../LayoutManager';

export interface TitleSceneOptions {
  on1Player: () => void;
  on2Player: () => void;
  on2PlayerOnline: () => void;
}

/**
 * Title screen scene with logo, intro illustration, and 3 mode buttons
 * Logo positioned top-left, intro centered, mode buttons at bottom
 */
export class TitleScene extends BaseScene {
  private metrics: LayoutMetrics;
  private options: TitleSceneOptions;
  private backgroundSprite: Sprite | null = null;
  private logoSprite: Sprite | null = null;
  private introSprite: Sprite | null = null;
  private button1P: Sprite | null = null;
  private button2P: Sprite | null = null;
  private button2PO: Sprite | null = null;
  private muteButton: Container | null = null;
  private muteIcon: Text | null = null;

  constructor(metrics: LayoutMetrics, options: TitleSceneOptions) {
    super();
    this.metrics = metrics;
    this.options = options;
    this.build();
  }

  protected build(): void {
    // Create mute button (positioned absolutely)
    this.createMuteButton();

    // Load images
    this.loadAssets();
  }

  private createMuteButton(): void {
    this.muteButton = new Container();

    // Button background
    const bg = new Graphics();
    bg.circle(0, 0, 24);
    bg.fill({ color: COLORS.BG_SECONDARY, alpha: 0.8 });
    bg.stroke({ color: COLORS.TEXT_SECONDARY, width: 2 });
    this.muteButton.addChild(bg);

    // Icon (speaker emoji or muted)
    const iconStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: 20,
    });
    this.muteIcon = new Text({
      text: MusicManager.isMuted() ? '🔇' : '🔊',
      style: iconStyle,
    });
    this.muteIcon.anchor.set(0.5);
    this.muteButton.addChild(this.muteIcon);

    // Interaction
    this.muteButton.eventMode = 'static';
    this.muteButton.cursor = 'pointer';

    // Accessibility
    this.muteButton.accessible = true;
    this.muteButton.accessibleType = 'button';
    this.muteButton.accessibleTitle = 'Toggle sound';

    this.muteButton.on('pointerover', () => {
      gsap.to(this.muteButton!.scale, { x: 1.1, y: 1.1, duration: 0.1 });
    });
    this.muteButton.on('pointerout', () => {
      gsap.to(this.muteButton!.scale, { x: 1, y: 1, duration: 0.1 });
    });
    this.muteButton.on('pointerdown', () => {
      this.toggleMute();
    });

    this.container.addChild(this.muteButton);
    this.positionMuteButton();
  }

  private positionMuteButton(): void {
    if (this.muteButton) {
      this.muteButton.x = this.metrics.width - SPACING.LG - 24;
      this.muteButton.y = SPACING.LG + 24;
    }
  }

  private toggleMute(): void {
    const isMuted = MusicManager.toggleMute();
    if (this.muteIcon) {
      this.muteIcon.text = isMuted ? '🔇' : '🔊';
    }
  }

  private async loadAssets(): Promise<void> {
    try {
      const [backgroundTexture, logoTexture, introTexture, button1PTexture, button2PTexture, button2POTexture] = await Promise.all([
        Assets.load('/assets/background.png'),
        Assets.load('/assets/MW_logo.png'),
        Assets.load('/assets/intro.png'),
        Assets.load('/assets/1P_button.png'),
        Assets.load('/assets/2P_button.png'),
        Assets.load('/assets/2PO_button.png'),
      ]);

      // Background sprite - stretched to fill screen
      this.backgroundSprite = new Sprite(backgroundTexture);
      this.backgroundSprite.anchor.set(0, 0);
      this.container.addChildAt(this.backgroundSprite, 0);

      // Logo sprite - positioned top-left
      this.logoSprite = new Sprite(logoTexture);
      this.logoSprite.anchor.set(0, 0);
      this.container.addChild(this.logoSprite);

      // Intro illustration sprite - centered
      this.introSprite = new Sprite(introTexture);
      this.introSprite.anchor.set(0.5);
      this.container.addChild(this.introSprite);

      // Create mode buttons
      this.button1P = this.createModeButton(button1PTexture, '1 Player vs AI', () => this.options.on1Player());
      this.button2P = this.createModeButton(button2PTexture, '2 Player Local', () => this.options.on2Player());
      this.button2PO = this.createModeButton(button2POTexture, '2 Player Online', () => this.options.on2PlayerOnline());

      this.container.addChild(this.button1P);
      this.container.addChild(this.button2P);
      this.container.addChild(this.button2PO);

      // Position everything
      this.layoutElements();

      // Fade in images
      this.logoSprite.alpha = 0;
      this.introSprite.alpha = 0;
      this.button1P.alpha = 0;
      this.button2P.alpha = 0;
      this.button2PO.alpha = 0;
      gsap.to(this.logoSprite, { alpha: 1, duration: 0.5 });
      gsap.to(this.introSprite, { alpha: 1, duration: 0.5, delay: 0.2 });
      gsap.to(this.button1P, { alpha: 1, duration: 0.5, delay: 0.4 });
      gsap.to(this.button2P, { alpha: 1, duration: 0.5, delay: 0.5 });
      gsap.to(this.button2PO, { alpha: 1, duration: 0.5, delay: 0.6 });
    } catch (error) {
      console.error('Failed to load title assets:', error);
    }
  }

  private createModeButton(texture: import('pixi.js').Texture, accessibleTitle: string, onClick: () => void): Sprite {
    const button = new Sprite(texture);
    button.anchor.set(0.5);
    button.eventMode = 'static';
    button.cursor = 'pointer';

    // Accessibility
    button.accessible = true;
    button.accessibleType = 'button';
    button.accessibleTitle = accessibleTitle;

    // Hover effect
    button.on('pointerover', () => {
      const scale = this.getModeButtonScale();
      gsap.to(button.scale, { x: scale * 1.08, y: scale * 1.08, duration: 0.15 });
    });
    button.on('pointerout', () => {
      const scale = this.getModeButtonScale();
      gsap.to(button.scale, { x: scale, y: scale, duration: 0.15 });
    });

    // Press effect and callback
    button.on('pointerdown', () => {
      const scale = this.getModeButtonScale();
      gsap.to(button.scale, { x: scale * 0.95, y: scale * 0.95, duration: 0.1 });
    });
    button.on('pointerup', () => {
      const scale = this.getModeButtonScale();
      gsap.to(button.scale, { x: scale, y: scale, duration: 0.1 });
      onClick();
    });
    button.on('pointerupoutside', () => {
      const scale = this.getModeButtonScale();
      gsap.to(button.scale, { x: scale, y: scale, duration: 0.1 });
    });

    return button;
  }

  private layoutElements(): void {
    const { width, height } = this.metrics;
    const isLandscape = width > height;

    // Background - stretch to fill screen
    if (this.backgroundSprite) {
      this.backgroundSprite.width = width;
      this.backgroundSprite.height = height;
    }

    // Logo - top left, bigger size
    let logoBottom = SPACING.LG;
    if (this.logoSprite) {
      const maxLogoWidth = Math.min(width * 0.6, 500);
      const maxLogoHeight = height * 0.25;
      const logoScale = Math.min(
        maxLogoWidth / this.logoSprite.texture.width,
        maxLogoHeight / this.logoSprite.texture.height
      );
      this.logoSprite.scale.set(logoScale);
      this.logoSprite.x = SPACING.LG;
      this.logoSprite.y = SPACING.LG;
      logoBottom = SPACING.LG + this.logoSprite.height;
    }

    // Mode buttons - positioned at bottom (calculate first to know buttonY)
    const buttonScale = this.getModeButtonScale();
    const buttonGap = isLandscape ? SPACING.MD : SPACING.SM;
    let buttonY = height - SPACING.XL - 30;
    let buttonHeight = 0;

    if (this.button1P && this.button2P && this.button2PO) {
      this.button1P.scale.set(buttonScale);
      this.button2P.scale.set(buttonScale);
      this.button2PO.scale.set(buttonScale);

      const buttonWidth = this.button1P.width;
      buttonHeight = this.button1P.height;
      const totalWidth = buttonWidth * 3 + buttonGap * 2;
      const startX = (width - totalWidth) / 2 + buttonWidth / 2;

      this.button1P.x = startX;
      this.button1P.y = buttonY;

      this.button2P.x = startX + buttonWidth + buttonGap;
      this.button2P.y = buttonY;

      this.button2PO.x = startX + (buttonWidth + buttonGap) * 2;
      this.button2PO.y = buttonY;
    }

    // Intro - vertically centered between logo bottom and button top
    if (this.introSprite) {
      const buttonTop = buttonY - buttonHeight / 2;
      const availableHeight = buttonTop - logoBottom;
      const introCenterY = logoBottom + availableHeight / 2;

      const maxIntroWidth = width * 0.7;
      const maxIntroHeight = availableHeight * 0.85; // Leave some padding
      const introScale = Math.min(
        maxIntroWidth / this.introSprite.texture.width,
        maxIntroHeight / this.introSprite.texture.height
      );
      this.introSprite.scale.set(introScale);
      this.introSprite.x = width / 2;
      this.introSprite.y = introCenterY;
    }

    // Mute button position
    this.positionMuteButton();
  }

  private getModeButtonScale(): number {
    const { width } = this.metrics;
    // Each button should be about 1/4 of screen width max
    const maxButtonWidth = Math.min(width * 0.25, 180);
    if (!this.button1P) return 1;
    return maxButtonWidth / this.button1P.texture.width;
  }

  onEnter(): void {
    super.onEnter();

    // Animate mode buttons with a subtle fade when entering
    if (this.button1P && this.button2P && this.button2PO) {
      this.button1P.alpha = 0;
      this.button2P.alpha = 0;
      this.button2PO.alpha = 0;
      gsap.to(this.button1P, { alpha: 1, duration: 0.5, delay: 0.3 });
      gsap.to(this.button2P, { alpha: 1, duration: 0.5, delay: 0.4 });
      gsap.to(this.button2PO, { alpha: 1, duration: 0.5, delay: 0.5 });
    }
  }

  onResize(metrics: LayoutMetrics): void {
    this.metrics = metrics;
    this.layoutElements();
  }
}
